import { Flex, Form, Card, Typography, Button, Spin } from 'antd'
import { FC, useEffect, useState } from 'react'
import { relayStore } from '../store/relay'
import {
	ElectricMechanic
} from '../helpers/types/relay'
import { useNavigate, useParams } from 'react-router-dom'
import { RNRelayOneRecords, RNRelay, RNRelayType } from '../helpers/routeNames'
import { PageBreadcrumb } from '../components/PageBreadcrumb'
import { CharacteristicCard } from '../components/CharacteristicCard'
import { CharacteristicInput } from '../components/CharacteristicInput'
import { updateRecordStatus } from '../api/relay'

export const MechanicValue: FC = () => {
	const {
		relayTypes,
		relayOnes,
		createMechanicValue,
		checkRecordMechanicValue,
		fetchRelayOnesByRelayId
	} = relayStore()
	const [form] = Form.useForm()
	const { Title } = Typography

	const { relayId, relayTypeId, relayOneId, recordId } = useParams<{
		relayId: string
		relayTypeId: string
		relayOneId: string
		recordId: string
	}>()

	const navigate = useNavigate()

	const [loading, setLoading] = useState(true)
	const [isDublicate, setIsDublicate] = useState<boolean>(false)

	// Derive directly from store — no stale useState copies
	const relayType = relayTypes.find((rt) => rt.id === relayTypeId)
	const relay = relayType?.relays.find((r) => r.id === relayId)
	const relayOne = relayOnes.find((ro) => ro.id === relayOneId)

	useEffect(() => {
		if (!relayId || !relayOneId) return
		let cancelled = false
		setLoading(true)
		fetchRelayOnesByRelayId(relayId).finally(() => {
			if (!cancelled) setLoading(false)
		})
		return () => { cancelled = true }
	}, [relayId, relayOneId, fetchRelayOnesByRelayId])

	useEffect(() => {
		if (!relayTypeId || !relayId || !relayOneId || !recordId)
			return
		const fetch = async () => {
			const data = await checkRecordMechanicValue(
				relayTypeId,
				relayId,
				relayOneId,
				recordId
			)
			setIsDublicate(data.dublicate)
		}
		fetch()
	}, [checkRecordMechanicValue, relayId, relayOneId, relayTypeId, recordId])

	if (loading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
				<Spin size="large" />
			</div>
		)
	}

	if (
		!relay ||
		!relayType ||
		!relayOne ||
		!relayOneId ||
		!relayId ||
		!relayTypeId ||
		!recordId
	) {
		return <div>Реле, тип реле или реле не найдено</div>
	}

	if (isDublicate) {
		return <div>Дубликат записи</div>
	}

	return (
		<Flex vertical>
			<PageBreadcrumb
				items={[
					{ label: 'Типы реле', path: RNRelayType },
					{ label: relayType.name, path: RNRelayType + '/' + relayTypeId },
					{ label: relay.name, path: RNRelay + '/' + relayId },
					{
						label: 'Записи',
						path: `${RNRelayOneRecords}/${relayTypeId}/${relayId}/${relayOneId}`
					},
					{ label: 'Мех. значения' }
				]}
			/>
			<header
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center'
				}}>
				<Title level={2}>
					Добавление механических значении к {relay.name}
				</Title>
			</header>
			<main>
				<CharacteristicCard
					title="Информация об экземпляре реле"
					data={[
						{ label: 'Тип реле', value: relayType.name },
						{
							label: 'Год выпуска',
							value: relayOne.madeYear
						},
						{
							label: 'Уникальный номер',
							value: relayOne.relayUnique
						},
						{
							label: 'Срок службы',
							value: relay.expiration
						},
						{
							label: 'Дата создания',
							value: new Date(
								relayOne.createdAt
							).toLocaleString('ru')
						}
					]}
				/>

				<Card
					title="Механические характеристики"
					style={{ marginTop: 24 }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={async (
							values: Record<
								ElectricMechanic['id'],
								string
							>
						) => {
							const data = Object.entries(values).map(
								([key, value]) => ({
									mechanicId: key,
									value,
									relayOneId: relayOne.id,
									relayTypeId,
									relayId,
									recordId
								})
							)
							const res =
								await createMechanicValue(data)
							if (res) {
								if (recordId)
									await updateRecordStatus(
										recordId,
										{ status: 'mechanic' }
									)
								navigate(
									`${RNRelayOneRecords}/${relayTypeId}/${relayId}/${relayOneId}/${recordId}`
								)
							}
						}}>
						{(() => {
							const parentIds = new Set(
								relayType.mechanics
									.filter((m) => m.parentId)
									.map((m) => m.parentId as string)
							)
							return relayType.mechanics
								.filter((m) => !parentIds.has(m.id))
								.sort(
									(a, b) =>
										a.orderInLevel - b.orderInLevel
								)
								.map((mechanic) => {
									const parentMechanic =
										relayType.mechanics.find(
											(m) =>
												m.id ===
												mechanic.parentId
										) || null
									return (
										<CharacteristicInput
											key={mechanic.id}
											label={
												parentMechanic
													? `${parentMechanic.name} / ${mechanic.name}`
													: mechanic.name
											}
											measurement={
												mechanic.measurement
											}
											fieldName={mechanic.id}
											placeholder="Введите значение"
										/>
									)
								})
						})()}
						<Form.Item>
							<Button type="primary" htmlType="submit">
								Сохранить
							</Button>
						</Form.Item>
					</Form>
				</Card>
			</main>
		</Flex>
	)
}
